import { useDataExplorerAppearance } from './appearanceContext';
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useEffect, useMemo, useState } from "react";
import type { DragEvent, ReactNode } from "react";

import { useMonkeysLocaleMessages } from '../../provider/context';
import { cn } from '../base/utils';
import {
  getDataExplorerMenuItemToneClassName,
  resolveDataExplorerAppearance,
} from "./theme";
import type {
  DataExplorerViewTreeAction,
  DataExplorerViewTreeActionContext,
  DataExplorerViewTreeGroup,
  DataExplorerViewTreeNode,
  DataExplorerViewTreeProps,
  DataExplorerViewTreeReorderPayload,
} from './types';
import { resolveDataExplorerValue } from './utils';

type TreeGroup<TGroupSource, TNodeSource, TContext> = DataExplorerViewTreeGroup<
  TGroupSource,
  TNodeSource,
  TContext
>;

type TreeNode<
  TNodeSource,
  TGroupSource = unknown,
  TContext = unknown,
> = DataExplorerViewTreeNode<TNodeSource, TGroupSource, TContext>;

type FlattenedNode<TNodeSource, TGroupSource, TContext> = {
  node: TreeNode<TNodeSource, TGroupSource, TContext>;
  group?: TreeGroup<TGroupSource, TNodeSource, TContext>;
  parentId?: string;
  index: number;
};

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={cn(
        "h-3.5 w-3.5 transition-transform",
        expanded && "rotate-90",
      )}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <circle cx="9" cy="6" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="18" r="1" />
      <circle cx="15" cy="6" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="18" r="1" />
    </svg>
  );
}

function collectDefaultExpandedNodeIds<TNodeSource, TGroupSource, TContext>(
  nodes: Array<TreeNode<TNodeSource, TGroupSource, TContext>> | undefined,
  defaults: Set<string>,
) {
  for (const node of nodes ?? []) {
    if (node.defaultExpanded) {
      defaults.add(node.id);
    }
    collectDefaultExpandedNodeIds(node.children, defaults);
  }
}

function buildDefaultNodeExpandedSet<TNodeSource, TGroupSource, TContext>(
  groups: Array<TreeGroup<TGroupSource, TNodeSource, TContext>>,
  defaultExpandedNodeIds: string[] | undefined,
) {
  const defaults = new Set(defaultExpandedNodeIds ?? []);
  for (const group of groups) {
    collectDefaultExpandedNodeIds(group.nodes, defaults);
  }
  return defaults;
}

function buildDefaultGroupExpandedSet<TNodeSource, TGroupSource, TContext>(
  groups: Array<TreeGroup<TGroupSource, TNodeSource, TContext>>,
  defaultExpandedGroupIds: string[] | undefined,
) {
  const defaults = new Set(defaultExpandedGroupIds ?? []);
  for (const group of groups) {
    if (group.hideHeader || group.defaultExpanded !== false) {
      defaults.add(group.id);
    }
  }
  return defaults;
}

function flattenNodes<TNodeSource, TGroupSource, TContext>(
  nodes: Array<TreeNode<TNodeSource, TGroupSource, TContext>> | undefined,
  group: TreeGroup<TGroupSource, TNodeSource, TContext> | undefined,
  parentId: string | undefined,
  entries: Map<string, FlattenedNode<TNodeSource, TGroupSource, TContext>>,
) {
  for (const [index, node] of (nodes ?? []).entries()) {
    entries.set(node.id, { node, group, parentId, index });
    flattenNodes(node.children, group, node.id, entries);
  }
}

function buildFlattenedNodeMap<TNodeSource, TGroupSource, TContext>(
  groups: Array<TreeGroup<TGroupSource, TNodeSource, TContext>>,
) {
  const entries = new Map<
    string,
    FlattenedNode<TNodeSource, TGroupSource, TContext>
  >();
  for (const group of groups) {
    flattenNodes(group.nodes, group, undefined, entries);
  }
  return entries;
}

function getNodeActions<TNodeSource, TGroupSource, TContext>(
  node: TreeNode<TNodeSource, TGroupSource, TContext>,
  globalActions:
    | Array<DataExplorerViewTreeAction<TNodeSource, TGroupSource, TContext>>
    | undefined,
) {
  return [
    ...(globalActions ?? []),
    ...((node.actions ?? []) as Array<
      DataExplorerViewTreeAction<TNodeSource, TGroupSource, TContext>
    >),
  ];
}

function getGroupActions<TNodeSource, TGroupSource, TContext>(
  group: TreeGroup<TGroupSource, TNodeSource, TContext>,
  globalActions:
    | Array<DataExplorerViewTreeAction<TNodeSource, TGroupSource, TContext>>
    | undefined,
) {
  return [
    ...(globalActions ?? []),
    ...((group.actions ?? []) as Array<
      DataExplorerViewTreeAction<TNodeSource, TGroupSource, TContext>
    >),
  ];
}

export function DataExplorerViewTree<
  TNodeSource = unknown,
  TGroupSource = unknown,
  TContext = unknown,
>({
  groups,
  nodes,
  context,
  selectedNodeId,
  selectedGroupId,
  expandedNodeIds,
  defaultExpandedNodeIds,
  expandedGroupIds,
  defaultExpandedGroupIds,
  resetExpansionKey,
  nodeActions,
  groupActions,
  indentSize = 16,
  indentOffset = 8,
  actionIcon,
  empty,
  loading = false,
  loadingNode,
  error,
  labels,
  reorderMode = "none",
  appearance,
  className,
  classNames,
  onSelectNode,
  onSelectGroup,
  onExpandedNodeIdsChange,
  onExpandedGroupIdsChange,
  onAction,
  onReorderNodes,
}: DataExplorerViewTreeProps<TNodeSource, TGroupSource, TContext>) {
  const internalLabels = useMonkeysLocaleMessages().dataExplorer.viewTree;
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const actionLabel = labels?.actions ?? internalLabels.actions;
  const expandLabel = labels?.expand ?? internalLabels.expand;
  const collapseLabel = labels?.collapse ?? internalLabels.collapse;
  const loadingContent =
    loadingNode ?? labels?.loading ?? internalLabels.loading;
  const emptyContent = empty ?? labels?.empty ?? internalLabels.empty;
  const hasExplicitGroups = Array.isArray(groups);
  const resolvedGroups = useMemo<
    Array<TreeGroup<TGroupSource, TNodeSource, TContext>>
  >(() => {
    if (hasExplicitGroups) {
      return groups;
    }
    return [
      {
        id: "__root__",
        hideHeader: true,
        defaultExpanded: true,
        nodes: Array.isArray(nodes) ? nodes : [],
      },
    ];
  }, [groups, hasExplicitGroups, nodes]);
  const flattenedNodeMap = useMemo(
    () => buildFlattenedNodeMap(resolvedGroups),
    [resolvedGroups],
  );
  const [internalExpandedNodeIds, setInternalExpandedNodeIds] = useState(() =>
    buildDefaultNodeExpandedSet(resolvedGroups, defaultExpandedNodeIds),
  );
  const [internalExpandedGroupIds, setInternalExpandedGroupIds] = useState(() =>
    buildDefaultGroupExpandedSet(resolvedGroups, defaultExpandedGroupIds),
  );
  const [draggingNodeId, setDraggingNodeId] = useState<string | undefined>();
  const [dragOverNodeId, setDragOverNodeId] = useState<string | undefined>();

  useEffect(() => {
    setInternalExpandedNodeIds(
      buildDefaultNodeExpandedSet(resolvedGroups, defaultExpandedNodeIds),
    );
    setInternalExpandedGroupIds(
      buildDefaultGroupExpandedSet(resolvedGroups, defaultExpandedGroupIds),
    );
  }, [defaultExpandedGroupIds, defaultExpandedNodeIds, resetExpansionKey]);

  const resolvedExpandedNodeIds =
    expandedNodeIds !== undefined
      ? new Set(expandedNodeIds)
      : internalExpandedNodeIds;
  const resolvedExpandedGroupIds =
    expandedGroupIds !== undefined
      ? new Set(expandedGroupIds)
      : internalExpandedGroupIds;

  const setNodeExpanded = (nodeId: string, expanded: boolean) => {
    const next = new Set(resolvedExpandedNodeIds);
    if (expanded) {
      next.add(nodeId);
    } else {
      next.delete(nodeId);
    }
    if (expandedNodeIds === undefined) {
      setInternalExpandedNodeIds(next);
    }
    onExpandedNodeIdsChange?.([...next]);
  };

  const setGroupExpanded = (groupId: string, expanded: boolean) => {
    const next = new Set(resolvedExpandedGroupIds);
    if (expanded) {
      next.add(groupId);
    } else {
      next.delete(groupId);
    }
    if (expandedGroupIds === undefined) {
      setInternalExpandedGroupIds(next);
    }
    onExpandedGroupIdsChange?.([...next]);
  };

  const createActionContext = (
    action: DataExplorerViewTreeAction<TNodeSource, TGroupSource, TContext>,
    target:
      | {
          type: "node";
          node: TreeNode<TNodeSource, TGroupSource, TContext>;
          group?: TreeGroup<TGroupSource, TNodeSource, TContext>;
        }
      | {
          type: "group";
          group: TreeGroup<TGroupSource, TNodeSource, TContext>;
        },
  ): DataExplorerViewTreeActionContext<TNodeSource, TGroupSource, TContext> =>
    target.type === "node"
      ? {
          type: "node",
          action,
          node: target.node,
          group: target.group,
          context,
        }
      : {
          type: "group",
          action,
          group: target.group,
          context,
        };

  const renderActions = (
    actions: Array<
      DataExplorerViewTreeAction<TNodeSource, TGroupSource, TContext>
    >,
    target:
      | {
          type: "node";
          node: TreeNode<TNodeSource, TGroupSource, TContext>;
          group?: TreeGroup<TGroupSource, TNodeSource, TContext>;
        }
      | {
          type: "group";
          group: TreeGroup<TGroupSource, TNodeSource, TContext>;
        },
  ) => {
    const visibleActions = actions.filter((action) => {
      const payload = createActionContext(action, target);
      return resolveDataExplorerValue(action.visible, payload) ?? true;
    });
    if (visibleActions.length === 0) return null;

    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            title={actionLabel}
            aria-label={actionLabel}
            className={cn(
              theme.slots.menuTrigger,
              target.type === "group"
                ? theme.slots.treeGroupActions
                : theme.slots.treeNodeActions,
              target.type === "group"
                ? classNames?.groupActions
                : cn(classNames?.nodeActions, classNames?.itemActions),
            )}
            onClick={(event) => event.stopPropagation()}
          >
            {actionIcon ?? <MoreIcon />}
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className={cn(theme.slots.menuContent, classNames?.menuContent)}
            style={theme.styles.menuContent}
            onClick={(event) => event.stopPropagation()}
          >
            {visibleActions.map((action) => {
              const payload = createActionContext(action, target);
              const disabled =
                (resolveDataExplorerValue(action.disabled, payload) ?? false) ||
                (resolveDataExplorerValue(action.loading, payload) ?? false);
              const active =
                resolveDataExplorerValue(action.active, payload) ?? false;

              if (action.render) {
                return <div key={action.id}>{action.render(payload)}</div>;
              }

              return (
                <div key={action.id}>
                  {action.separatorBefore ? (
                    <DropdownMenu.Separator
                      className={theme.slots.menuSeparator}
                    />
                  ) : null}
                  <DropdownMenu.Item
                    title={action.title}
                    disabled={disabled}
                    className={cn(
                      theme.slots.menuItem,
                      getDataExplorerMenuItemToneClassName(theme, action.tone),
                      active && "bg-accent text-accent-foreground",
                      classNames?.menuItem,
                    )}
                    style={theme.styles.menuItem}
                    onSelect={() => {
                      if (disabled) return;
                      void action.onAction?.(payload);
                      void onAction?.(payload);
                    }}
                  >
                    {action.icon ? (
                      <span className={theme.slots.menuItemIcon}>
                        {action.icon}
                      </span>
                    ) : null}
                    <span className={theme.slots.menuItemLabel}>
                      {action.label}
                    </span>
                  </DropdownMenu.Item>
                  {action.separatorAfter ? (
                    <DropdownMenu.Separator
                      className={theme.slots.menuSeparator}
                    />
                  ) : null}
                </div>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    );
  };

  const handleDrop = (
    event: DragEvent<HTMLElement>,
    overNode: TreeNode<TNodeSource, TGroupSource, TContext>,
  ) => {
    event.preventDefault();
    const activeNodeId =
      draggingNodeId ||
      event.dataTransfer.getData("application/x-data-explorer-node");
    if (!activeNodeId || activeNodeId === overNode.id || !onReorderNodes) {
      setDraggingNodeId(undefined);
      setDragOverNodeId(undefined);
      return;
    }

    const activeEntry = flattenedNodeMap.get(activeNodeId);
    const overEntry = flattenedNodeMap.get(overNode.id);
    if (!activeEntry || !overEntry) {
      setDraggingNodeId(undefined);
      setDragOverNodeId(undefined);
      return;
    }

    const sameParent =
      activeEntry.parentId === overEntry.parentId &&
      activeEntry.group?.id === overEntry.group?.id;
    if (reorderMode === "same-parent" && !sameParent) {
      setDraggingNodeId(undefined);
      setDragOverNodeId(undefined);
      return;
    }

    const payload: DataExplorerViewTreeReorderPayload<
      TNodeSource,
      TGroupSource,
      TContext
    > = {
      activeNode: activeEntry.node,
      overNode: overEntry.node,
      activeGroup: activeEntry.group,
      overGroup: overEntry.group,
      activeParentId: activeEntry.parentId,
      overParentId: overEntry.parentId,
      oldIndex: activeEntry.index,
      newIndex: overEntry.index,
      sameParent,
      context,
    };
    void onReorderNodes(payload);
    setDraggingNodeId(undefined);
    setDragOverNodeId(undefined);
  };

  const renderNode = (
    node: TreeNode<TNodeSource, TGroupSource, TContext>,
    group: TreeGroup<TGroupSource, TNodeSource, TContext> | undefined,
    depth: number,
  ): ReactNode => {
    const childNodes = Array.isArray(node.children) ? node.children : [];
    const hasChildren = childNodes.length > 0;
    const isExpanded =
      node.collapsible === false || resolvedExpandedNodeIds.has(node.id);
    const isSelected = selectedNodeId === node.id;
    const isSelectable = node.selectable !== false && !node.disabled;
    const isReorderable =
      reorderMode !== "none" && !!onReorderNodes && node.reorderable !== false;
    const isDragging = draggingNodeId === node.id;
    const isDragOver = dragOverNodeId === node.id && draggingNodeId !== node.id;
    const actions = getNodeActions(node, nodeActions);

    return (
      <div
        key={node.id}
        data-data-explorer-entry="node"
        data-data-explorer-node-id={node.id}
        data-data-explorer-group-id={group?.id}
        className={cn(
          theme.slots.treeNode,
          classNames?.node,
          classNames?.item,
          node.className,
        )}
        onDragOver={(event) => {
          if (!draggingNodeId || !isReorderable) return;
          event.preventDefault();
          setDragOverNodeId(node.id);
        }}
        onDragLeave={() => {
          if (dragOverNodeId === node.id) {
            setDragOverNodeId(undefined);
          }
        }}
        onDrop={(event) => handleDrop(event, node)}
      >
        <div
          className={cn(
            theme.slots.treeNodeButton,
            isSelected && theme.slots.treeNodeSelected,
            node.disabled && theme.slots.treeNodeDisabled,
            isDragging && theme.slots.treeNodeDragging,
            isDragOver && "ring-1 ring-primary/30",
            isSelected && classNames?.itemSelected,
            isSelected && classNames?.nodeSelected,
            node.disabled && classNames?.nodeDisabled,
            isDragging && classNames?.nodeDragging,
            classNames?.nodeButton,
            classNames?.itemButton,
          )}
          style={{
            paddingLeft: `calc(${(node.indentLevel ?? depth) * indentSize + indentOffset}px + var(--data-explorer-tree-group-indent, 0px))`,
          }}
          role="treeitem"
          aria-selected={isSelected || undefined}
          aria-disabled={node.disabled || undefined}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-label={node.ariaLabel}
          tabIndex={isSelectable ? 0 : -1}
          onClick={() => {
            if (!isSelectable) return;
            onSelectNode?.(node, context);
          }}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (!isSelectable) return;
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            onSelectNode?.(node, context);
          }}
        >
          {isReorderable ? (
            <span
              draggable
              className={cn(
                theme.slots.treeNodeDragHandle,
                classNames?.nodeDragHandle,
              )}
              onClick={(event) => event.stopPropagation()}
              onDragStart={(event) => {
                event.stopPropagation();
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData(
                  "application/x-data-explorer-node",
                  node.id,
                );
                setDraggingNodeId(node.id);
              }}
              onDragEnd={() => {
                setDraggingNodeId(undefined);
                setDragOverNodeId(undefined);
              }}
            >
              <GripIcon />
            </span>
          ) : null}
          {hasChildren ? (
            <button
              type="button"
              className={cn(
                theme.slots.treeNodeToggle,
                classNames?.nodeToggle,
                classNames?.expander,
              )}
              aria-label={isExpanded ? collapseLabel : expandLabel}
              onClick={(event) => {
                event.stopPropagation();
                setNodeExpanded(node.id, !isExpanded);
              }}
            >
              <ChevronIcon expanded={isExpanded} />
            </button>
          ) : (
            <span
              className={cn(theme.slots.treeNodeToggle, classNames?.nodeToggle)}
            />
          )}
          {node.icon ? (
            <span
              className={cn(
                theme.slots.treeNodeIcon,
                classNames?.nodeIcon,
                classNames?.itemIcon,
              )}
            >
              {node.icon}
            </span>
          ) : null}
          <span className="flex min-w-0 flex-1 flex-col">
            <span
              className={cn(
                theme.slots.treeNodeLabel,
                classNames?.nodeLabel,
                classNames?.itemLabel,
              )}
            >
              {node.label}
            </span>
            {node.description ? (
              <span
                className={cn(
                  theme.slots.treeNodeDescription,
                  classNames?.nodeDescription,
                )}
              >
                {node.description}
              </span>
            ) : null}
          </span>
          {node.meta ? (
            <span
              className={cn(theme.slots.treeNodeMeta, classNames?.nodeMeta)}
            >
              {node.meta}
            </span>
          ) : null}
          {node.badge ? (
            <span
              className={cn(theme.slots.treeNodeBadge, classNames?.nodeBadge)}
            >
              {node.badge}
            </span>
          ) : null}
          {renderActions(actions, { type: "node", node, group })}
        </div>
        {hasChildren && isExpanded ? (
          <div
            className={cn(
              theme.slots.treeNodeChildren,
              classNames?.nodeChildren,
              classNames?.list,
            )}
          >
            {childNodes.map((child) => renderNode(child, group, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderPlaceholder = (
    content: ReactNode,
    slotClassName: string,
    classNameOverride: string | undefined,
  ) => <div className={cn(slotClassName, classNameOverride)}>{content}</div>;

  if (loading) {
    return (
      <div className={cn(theme.slots.treeRoot, classNames?.root, className)}>
        {renderPlaceholder(
          loadingContent,
          theme.slots.treeLoading,
          classNames?.loading,
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(theme.slots.treeRoot, classNames?.root, className)}>
        {renderPlaceholder(
          error ?? labels?.error,
          theme.slots.treeError,
          classNames?.error,
        )}
      </div>
    );
  }

  const hasAnyNodes = resolvedGroups.some(
    (group) => (group.nodes ?? []).length > 0,
  );
  const hasAnyGroupState = resolvedGroups.some(
    (group) => group.status === "loading" || group.error,
  );
  const shouldRenderGlobalEmpty =
    !hasAnyNodes &&
    !hasAnyGroupState &&
    (!hasExplicitGroups || resolvedGroups.length === 0);
  if (shouldRenderGlobalEmpty) {
    return (
      <div className={cn(theme.slots.treeRoot, classNames?.root, className)}>
        {renderPlaceholder(
          emptyContent,
          theme.slots.treeEmpty,
          classNames?.empty,
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(theme.slots.treeRoot, classNames?.root, className)}
      role={hasAnyNodes || resolvedGroups.some((group) => !group.hideHeader) ? 'tree' : undefined}
    >
      {resolvedGroups.map((group) => {
        const groupExpanded =
          group.hideHeader ||
          group.collapsible === false ||
          resolvedExpandedGroupIds.has(group.id);
        const groupNodeCount = group.nodes?.length ?? 0;
        const actions = getGroupActions(group, groupActions);
        const groupSelected = selectedGroupId === group.id;
        const groupCollapsible = group.collapsible !== false;
        return (
          <section
            key={group.id}
            data-data-explorer-entry="group"
            data-data-explorer-group-id={group.id}
            className={cn(
              theme.slots.treeGroup,
              classNames?.group,
              group.className,
            )}
          >
            {!group.hideHeader ? (
              <div
                role="treeitem"
                aria-label={typeof group.label === 'string' ? group.label : undefined}
                aria-selected={group.selectable ? groupSelected : undefined}
                aria-expanded={groupCollapsible ? groupExpanded : undefined}
                className={cn(
                  theme.slots.treeGroupHeader,
                  theme.slots.treeGroupHeaderButton,
                  groupSelected && theme.slots.treeNodeSelected,
                  classNames?.groupHeader,
                  classNames?.groupHeaderButton,
                  groupSelected && classNames?.itemSelected,
                )}
              >
                <button
                  type="button"
                  className={cn(
                    "flex min-w-0 flex-1 items-center gap-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    classNames?.groupLabel,
                  )}
                  aria-pressed={group.selectable ? groupSelected : undefined}
                  aria-expanded={
                    !group.selectable && groupCollapsible
                      ? groupExpanded
                      : undefined
                  }
                  onClick={() => {
                    if (group.selectable) {
                      onSelectGroup?.(group, context);
                      return;
                    }
                    if (groupCollapsible)
                      setGroupExpanded(group.id, !groupExpanded);
                  }}
                >
                  {group.icon ? (
                    <span
                      className={cn(
                        theme.slots.treeNodeIcon,
                        classNames?.nodeIcon,
                        classNames?.itemIcon,
                      )}
                    >
                      {group.icon}
                    </span>
                  ) : null}
                  <span className="flex min-w-0 flex-1 flex-col">
                    {group.label ? (
                      <span
                        className={cn(
                          theme.slots.treeNodeLabel,
                          classNames?.nodeLabel,
                          classNames?.itemLabel,
                        )}
                      >
                        {group.label}
                      </span>
                    ) : null}
                    {group.description ? (
                      <span
                        className={cn(
                          theme.slots.treeNodeDescription,
                          classNames?.nodeDescription,
                        )}
                      >
                        {group.description}
                      </span>
                    ) : null}
                  </span>
                  {group.meta ? (
                    <span
                      className={cn(
                        theme.slots.treeNodeMeta,
                        classNames?.nodeMeta,
                      )}
                    >
                      {group.meta}
                    </span>
                  ) : null}
                  {group.badge ? (
                    <span
                      className={cn(
                        theme.slots.treeNodeBadge,
                        classNames?.nodeBadge,
                      )}
                    >
                      {group.badge}
                    </span>
                  ) : null}
                </button>
                {groupCollapsible ? (
                  <button
                    type="button"
                    className={cn(
                      theme.slots.treeNodeToggle,
                      classNames?.expander,
                    )}
                    aria-label={groupExpanded ? collapseLabel : expandLabel}
                    aria-expanded={groupExpanded}
                    onClick={() => setGroupExpanded(group.id, !groupExpanded)}
                  >
                    <ChevronIcon expanded={groupExpanded} />
                  </button>
                ) : (
                  <span className={cn(theme.slots.treeNodeToggle, classNames?.expander)} />
                )}
                {renderActions(actions, { type: "group", group })}
              </div>
            ) : null}
            {groupExpanded ? (
              <div
                role="group"
                className={cn(
                  theme.slots.treeGroupContent,
                  classNames?.groupContent,
                  classNames?.list,
                )}
              >
                {group.loading || group.status === "loading"
                  ? renderPlaceholder(
                      loadingContent,
                      theme.slots.treeLoading,
                      classNames?.loading,
                    )
                  : group.error || group.status === "error"
                    ? renderPlaceholder(
                        group.error ?? labels?.error ?? internalLabels.error,
                        theme.slots.treeError,
                        classNames?.error,
                      )
                    : groupNodeCount > 0
                      ? group.nodes?.map((node) => renderNode(node, group, 0))
                      : renderPlaceholder(
                          group.empty ?? emptyContent,
                          theme.slots.treeEmpty,
                          classNames?.empty,
                        )}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
